import { useState, useEffect, useCallback, useMemo } from 'react';
import { ReportDraft, ReportDraftFilters, ReportDraftItem } from '../../types/reportDrafts';
import { reportDraftsApi } from '../../api/reportDraftsApi';

interface UseReportDraftsReturn {
    drafts: { [key: string]: ReportDraft };
    /** Active drafts, most recently updated/created first. */
    activeDrafts: ReportDraft[];
    archivedDrafts: ReportDraft[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    createDraft: (name: string, filters: ReportDraftFilters, seedItems: { [eventId: string]: ReportDraftItem }) => Promise<ReportDraft>;
    renameDraft: (draftKey: string, name: string) => Promise<void>;
    duplicateDraft: (draftKey: string, newName: string) => Promise<ReportDraft>;
    setArchived: (draftKey: string, archived: boolean) => Promise<void>;
    deleteDraft: (draftKey: string) => Promise<void>;
    clearError: () => void;
}

const lastTouched = (draft: ReportDraft): string => draft.updatedAt || draft.createdAt;

/**
 * List + lifecycle hook for the drafts manager modal (plan D6).
 * One-time fetch, refetch after each mutation, like useTodos.
 */
export const useReportDrafts = (): UseReportDraftsReturn => {
    const [drafts, setDrafts] = useState<{ [key: string]: ReportDraft }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const activeDrafts = useMemo(() =>
        Object.values(drafts)
            .filter(d => !d.archived)
            .sort((a, b) => lastTouched(b).localeCompare(lastTouched(a))),
        [drafts]
    );

    const archivedDrafts = useMemo(() =>
        Object.values(drafts)
            .filter(d => d.archived)
            .sort((a, b) => lastTouched(b).localeCompare(lastTouched(a))),
        [drafts]
    );

    const loadDrafts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const loaded = await reportDraftsApi.getDrafts();
            setDrafts(loaded);
        } catch (err) {
            console.error('Error loading report drafts:', err);
            setError('Kunde inte ladda rapportutkasten');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    const run = useCallback(async <T,>(action: () => Promise<T>, fallbackMessage: string): Promise<T> => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await loadDrafts();
            return result;
        } catch (err: any) {
            console.error('Report draft action failed:', err);
            setError(err.message || fallbackMessage);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [loadDrafts]);

    const createDraft = useCallback((name: string, filters: ReportDraftFilters, seedItems: { [eventId: string]: ReportDraftItem }) =>
        run(() => reportDraftsApi.createDraft(name, filters, seedItems), 'Kunde inte skapa utkastet'),
    [run]);

    const renameDraft = useCallback((draftKey: string, name: string) =>
        run(() => reportDraftsApi.renameDraft(draftKey, name), 'Kunde inte byta namn på utkastet'),
    [run]);

    const duplicateDraft = useCallback((draftKey: string, newName: string) =>
        run(() => reportDraftsApi.duplicateDraft(draftKey, newName), 'Kunde inte kopiera utkastet'),
    [run]);

    const setArchived = useCallback((draftKey: string, archived: boolean) =>
        run(() => reportDraftsApi.setArchived(draftKey, archived), 'Kunde inte arkivera utkastet'),
    [run]);

    const deleteDraft = useCallback((draftKey: string) =>
        run(() => reportDraftsApi.deleteDraft(draftKey), 'Kunde inte radera utkastet'),
    [run]);

    const clearError = useCallback(() => setError(null), []);

    return {
        drafts,
        activeDrafts,
        archivedDrafts,
        loading,
        error,
        submitting,
        createDraft,
        renameDraft,
        duplicateDraft,
        setArchived,
        deleteDraft,
        clearError,
    };
};
